<script lang="ts">
  import { onMount } from 'svelte';
  import { _ } from 'svelte-i18n';
  import { fade, fly } from 'svelte/transition';
  import Header from '$lib/components/sections/Header.svelte';
  import Footer from '$lib/components/sections/Footer.svelte';
  import WeeklySearch from '$lib/components/weekly/WeeklySearch.svelte';
  import RSSDropdown from '$lib/components/weekly/RSSDropdown.svelte';
  import Pagination from '$lib/components/primitives/Pagination.svelte';
  import { formatReadingTime } from '$lib/utils/formatReadingTime';
  import type { PageData } from './$types';
  
  let { data }: { data: PageData } = $props();
  
  let visible = $state(true); // SSR: start visible so hero + <h1> are in server HTML (Ahrefs: "H1 tag missing"). onMount re-set is a no-op; entrance animations still play on client-side navigation.
  let searchQuery = $state('');
  let selectedTag = $state('all');
  
  // Pagination settings
  const POSTS_PER_PAGE = 9; // Show 9 posts per page
  let currentPage = $state(1);
  
  // Format date according to locale
  function formatDate(dateString: string): string {
    const date = new Date(dateString);
    const localeCode = data.locale === 'en' ? 'en-US' : data.locale;
    
    return date.toLocaleDateString(localeCode, {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  }
  
  // Tags for filtering - show most popular tags with counts
  const tags = $derived([
    { id: 'all', label: $_('weekly.blog.tags.all'), count: data.posts?.length || 0 },
    ...(data.tags || []).map(tag => ({ 
      id: tag.name.toLowerCase().replace(/\s+/g, '-'), 
      label: tag.name,
      count: tag.count 
    }))
  ]);
  
  // Determine how many tags to show based on viewport
  let maxVisibleTags = $state(5); // Default for desktop - more reasonable number
  
  onMount(() => {
    visible = true;
    
    // Adjust number of visible tags based on screen size
    const updateMaxTags = () => {
      if (window.innerWidth < 640) {
        maxVisibleTags = 4; // Mobile - show 3 + "All"
      } else if (window.innerWidth < 1024) {
        maxVisibleTags = 5; // Tablet
      } else {
        maxVisibleTags = 6; // Desktop - keep it clean
      }
    };
    
    updateMaxTags();
    window.addEventListener('resize', updateMaxTags);
    
    return () => {
      window.removeEventListener('resize', updateMaxTags);
    };
  });
  
  // Filter posts based on tag and search
  let filteredPosts = $derived((data.posts || []).filter(post => {
    const matchesTag = selectedTag === 'all' || 
      (post.tags || []).some(tag => tag.toLowerCase().replace(/\s+/g, '-') === selectedTag);
    const matchesSearch = !searchQuery || 
      post.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      post.excerpt?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      post.author?.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesTag && matchesSearch;
  }));
  
  // Calculate pagination
  let nonFeaturedPosts = $derived(filteredPosts.filter(p => !p.featured));
  let totalPages = $derived(Math.ceil(nonFeaturedPosts.length / POSTS_PER_PAGE));
  
  // Debug logging
  $effect(() => {
    console.log(`Blog page - Total posts: ${data.posts.length}, Filtered: ${filteredPosts.length}, Non-featured: ${nonFeaturedPosts.length}, Pages: ${totalPages}`);
  });
  
  // Get posts to display for current page
  let postsToDisplay = $derived.by(() => {
    const startIndex = (currentPage - 1) * POSTS_PER_PAGE;
    const endIndex = startIndex + POSTS_PER_PAGE;
    return nonFeaturedPosts.slice(startIndex, endIndex);
  });
  
  // Handle page change
  function handlePageChange(page: number) {
    currentPage = page;
    // Scroll to top of posts section
    document.querySelector('.posts-grid')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
  
  // Reset pagination when filters change
  $effect(() => {
    // Reset when tag or search changes
    selectedTag;
    searchQuery;
    currentPage = 1;
  });
  
  // Get author info from authors data
  function getAuthorInfo(authorName: string) {
    const authorData = data.authors?.[authorName];
    return {
      name: authorName,
      slug: authorData?.slug || authorName.toLowerCase().replace(/\s+/g, '-'),
      profileImage: authorData?.profileImage,
      bio: authorData?.bio
    };
  }
  
  function handleSearch(query: string) {
    searchQuery = query;
  }
</script>

<svelte:head>
  <title>{$_('weekly.blog.meta.title')}</title>
  <meta name="description" content={$_('weekly.blog.meta.description')} />
	<meta property="og:title" content="{$_('weekly.blog.meta.title')}" />
	<meta property="og:description" content={$_('weekly.blog.meta.description')} />
</svelte:head>

<Header />

<main class="blog">
  {#if visible}
    <!-- Header Section -->
    <section class="header" in:fade={{ duration: 400 }}>
      <div class="container">
        <nav class="breadcrumb">
          <a href="/weekly" class="breadcrumb__link">{$_('weekly.hero.title')}</a>
          <span class="breadcrumb__separator">/</span>
          <span class="breadcrumb__current">{$_('weekly.categories.blog')}</span>
        </nav>
        
        <div class="header__content">
          <h1 class="header__title">{$_('weekly.categories.blog')}</h1>
          <p class="header__description">{$_('weekly.blog.description')}</p>
        </div>
        
        <!-- Controls -->
        <div class="controls-wrapper" in:fly={{ y: 20, duration: 500, delay: 100 }}>
          <!-- Single row with categories and search/RSS -->
          <div class="controls-row">
            <!-- Category pills on the left -->
            <div class="tag-pills-container">
              <div class="tag-pills">
              <!-- Always show "All" first -->
              <button
                class="tag-pill tag-pill--all"
                class:active={selectedTag === 'all'}
                onclick={() => selectedTag = 'all'}
                title="Show all posts ({data.posts?.length || 0})"
              >
                {$_('weekly.blog.tags.all')}
                <span class="tag-count">{data.posts?.length || 0}</span>
              </button>
              
              <!-- Popular categories -->
              {#each tags.slice(1, maxVisibleTags) as tag}
                <button
                  class="tag-pill"
                  class:active={selectedTag === tag.id}
                  onclick={() => selectedTag = tag.id}
                  title="{tag.label} ({tag.count} {tag.count === 1 ? 'post' : 'posts'})"
                >
                  {tag.label}
                  <span class="tag-count">{tag.count}</span>
                </button>
              {/each}
              
              <!-- Show more button if needed -->
              {#if tags.length > 6}
                <button
                  class="tag-pill tag-pill--more"
                  onclick={() => {
                    if (maxVisibleTags === tags.length) {
                      // Reset to default based on viewport
                      if (window.innerWidth < 640) {
                        maxVisibleTags = 4;
                      } else if (window.innerWidth < 1024) {
                        maxVisibleTags = 5;
                      } else {
                        maxVisibleTags = 6;
                      }
                    } else {
                      maxVisibleTags = tags.length;
                    }
                  }}
                  title={maxVisibleTags === tags.length ? "Show less" : "Show all categories"}
                >
                  {#if maxVisibleTags === tags.length}
                    Show less
                  {:else}
                    +{tags.length - maxVisibleTags} more
                  {/if}
                </button>
              {/if}
              </div>
            </div>
            
            <!-- Search and RSS on the right -->
            <div class="controls-right">
              <WeeklySearch onSearch={handleSearch} placeholder={$_('weekly.blog.search.placeholder')} />
              <RSSDropdown />
            </div>
          </div>
        </div>
      </div>
    </section>
    
    <!-- Featured Post (Latest) -->
    {#if filteredPosts.length > 0}
      {@const featured = filteredPosts[0]}
      {#if featured}
      {@const authorInfo = getAuthorInfo(featured.author)}
      <section class="featured" in:fly={{ y: 20, duration: 500, delay: 150 }}>
        <div class="container">
          <a href="/weekly/blog/{featured.slug}" class="featured-link">
            <article class="featured-post">
              <div class="featured-post__image">
                {#if featured.featureImage}
                  <img 
                    src={featured.featureImage} 
                    alt={featured.title}
                    loading="lazy"
                    onload={(e) => e.currentTarget.style.opacity = '1'}
                    onerror={(e) => {
                      e.currentTarget.style.display = 'none';
                      const placeholder = e.currentTarget.nextElementSibling;
                      if (placeholder) placeholder.style.display = 'flex';
                    }}
                    style="opacity: 0; transition: opacity 0.3s ease;"
                  />
                  <div class="image-placeholder" style="display: none;">
                    <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                      <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                      <circle cx="8.5" cy="8.5" r="1.5"></circle>
                      <polyline points="21 15 16 10 5 21"></polyline>
                    </svg>
                  </div>
                {:else}
                  <div class="image-placeholder">
                    <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                      <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                      <circle cx="8.5" cy="8.5" r="1.5"></circle>
                      <polyline points="21 15 16 10 5 21"></polyline>
                    </svg>
                  </div>
                {/if}
              </div>
              <div class="featured-post__content">
                <div class="featured-post__tags">
                  {#each featured.tags.slice(0, 3) as tag}
                    <span class="post-tag">{tag}</span>
                  {/each}
                </div>
                <h2 class="featured-post__title">{featured.title}</h2>
                <p class="featured-post__description">{featured.excerpt}</p>
                <div class="featured-post__meta">
                  <div class="author">
                    {#if authorInfo.profileImage}
                      <div class="author__avatar">
                        <img 
                          src={authorInfo.profileImage} 
                          alt={authorInfo.name}
                          loading="lazy"
                        />
                      </div>
                    {:else}
                      <div class="author__avatar">
                        <div class="avatar-placeholder"></div>
                      </div>
                    {/if}
                    <div class="author__info">
                      <div class="author__name" data-author-slug={authorInfo.slug}>{authorInfo.name}</div>
                      <div class="author__role">{featured.category}</div>
                    </div>
                  </div>
                  <div class="post-meta">
                    <span>{formatDate(featured.date)}</span>
                    <span class="separator">·</span>
                    <span>{formatReadingTime(featured.readingTime || 5, $_)}</span>
                  </div>
                </div>
              </div>
            </article>
          </a>
        </div>
      </section>
      {/if}
    {/if}
    
    <!-- Posts Grid -->
    <section class="posts" in:fly={{ y: 20, duration: 500, delay: 200 }}>
      <div class="container">
        {#if postsToDisplay.length > 0}
          <div class="posts__grid">
            {#each postsToDisplay as post, index}
              <a href="/weekly/blog/{post.slug}" class="post-link" in:fly={{ y: 20, duration: 400, delay: 50 + index * 30 }}>
                <article class="post-card">
                  <div class="post-card__image">
                    {#if post.featureImage}
                      <img 
                        src={post.featureImage} 
                        alt={post.title}
                        loading="lazy"
                        onload={(e) => e.currentTarget.style.opacity = '1'}
                        onerror={(e) => {
                          e.currentTarget.style.display = 'none';
                          const placeholder = e.currentTarget.nextElementSibling;
                          if (placeholder) placeholder.style.display = 'flex';
                        }}
                        style="opacity: 0; transition: opacity 0.3s ease;"
                      />
                      <div class="image-placeholder" style="display: none;">
                        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                          <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                          <circle cx="8.5" cy="8.5" r="1.5"></circle>
                          <polyline points="21 15 16 10 5 21"></polyline>
                        </svg>
                      </div>
                    {:else}
                      <div class="image-placeholder">
                        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                          <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                          <circle cx="8.5" cy="8.5" r="1.5"></circle>
                          <polyline points="21 15 16 10 5 21"></polyline>
                        </svg>
                      </div>
                    {/if}
                  </div>
                  <div class="post-card__content">
                    <div class="post-card__tags">
                      {#each post.tags.slice(0, 2) as tag}
                        <span class="post-tag-small">{tag}</span>
                      {/each}
                    </div>
                    <h3 class="post-card__title">{post.title}</h3>
                    <p class="post-card__description">{post.excerpt}</p>
                    <div class="post-card__footer">
                      <div class="post-card__author">
                        <span class="author-name">{post.author}</span>
                      </div>
                      <div class="post-card__meta">
                        <span>{formatDate(post.date)}</span>
                        <span class="separator">·</span>
                        <span>{formatReadingTime(post.readingTime || 5, $_)}</span>
                      </div>
                    </div>
                  </div>
                </article>
              </a>
            {/each}
          </div>
          
          {#if totalPages > 1}
            <div class="pagination-container">
              <Pagination 
                {currentPage}
                {totalPages}
                onPageChange={handlePageChange}
                showFirstLast={true}
                maxVisible={5}
              />
            </div>
          {/if}
        {:else}
          <div class="no-results">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <circle cx="11" cy="11" r="8"></circle>
              <path d="m21 21-4.35-4.35"></path>
            </svg>
            <h2 class="no-results__title">{$_('weekly.blog.noResults.title')}</h2>
            <p class="no-results__description">
              {$_('weekly.blog.noResults.description')}
            </p>
          </div>
        {/if}
      </div>
    </section>
  {/if}
</main>

<Footer />

<style>
  .blog {
    min-height: 100vh;
  }
  
  .container {
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 2rem;
  }
  
  /* Header */
  .header {
    padding: 1.5rem 0;
    background: linear-gradient(to bottom, var(--bg-primary), var(--bg-secondary));
    border-bottom: 1px solid var(--border-light);
  }
  
  .breadcrumb {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 0.75rem;
    margin-bottom: 1rem;
    opacity: 0.7;
  }
  
  .breadcrumb__link {
    color: var(--text-tertiary);
    text-decoration: none;
    transition: color 0.2s ease;
  }
  
  .breadcrumb__link:hover {
    color: var(--text-primary);
  }
  
  .breadcrumb__separator {
    color: var(--text-tertiary);
  }
  
  .breadcrumb__current {
    color: var(--text-primary);
  }
  
  .header__content {
    margin-bottom: 1.25rem;
    display: flex;
    align-items: baseline;
    gap: 1.5rem;
    flex-wrap: wrap;
  }
  
  .header__title {
    font-size: 1.75rem;
    font-weight: 600;
    color: var(--text-primary);
    letter-spacing: -0.025em;
    margin: 0;
  }
  
  .header__description {
    font-size: 0.9375rem;
    color: var(--text-tertiary);
    line-height: 1.4;
    margin: 0;
    flex: 1;
    min-width: 200px;
  }
  
  .controls-wrapper {
    display: flex;
    flex-direction: column;
    gap: 0;
  }
  
  .controls-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 2rem;
    min-height: 32px;
  }
  
  .tag-pills-container {
    display: flex;
    align-items: center;
    gap: 0;
    flex: 1;
    min-width: 0;
  }
  
  .tag-pills {
    display: flex;
    gap: 0.375rem;
    flex-wrap: wrap;
    align-items: center;
  }
  
  .tag-pill {
    padding: 0.25rem 0.625rem;
    background: transparent;
    border: 1px solid var(--border-light);
    border-radius: 100px;
    color: var(--text-secondary);
    font-size: 0.75rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.15s ease;
    white-space: nowrap;
    display: inline-flex;
    align-items: center;
    gap: 0.25rem;
  }
  
  .tag-pill--all {
    background: var(--bg-primary);
    border-color: var(--text-primary);
    color: var(--text-primary);
    font-weight: 600;
  }
  
  .tag-pill:hover {
    background: var(--bg-secondary);
    border-color: var(--text-secondary);
    color: var(--text-primary);
    transform: translateY(-1px);
  }
  
  .tag-pill.active {
    background: var(--text-primary);
    border-color: transparent;
    color: var(--bg-primary);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }
  
  .tag-count {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 1rem;
    height: 1rem;
    padding: 0 0.1875rem;
    background: rgba(0, 0, 0, 0.04);
    border-radius: 100px;
    font-size: 0.625rem;
    font-weight: 600;
    color: var(--text-tertiary);
    opacity: 0.7;
  }
  
  .tag-pill:hover .tag-count {
    background: rgba(0, 0, 0, 0.08);
  }
  
  .tag-pill.active .tag-count {
    background: rgba(255, 255, 255, 0.25);
    color: var(--bg-primary);
  }
  
  .tag-pill--more {
    background: transparent;
    border-style: dashed;
    color: var(--text-tertiary);
    font-weight: 400;
  }
  
  .tag-pill--more:hover {
    background: var(--bg-secondary);
    border-style: solid;
    color: var(--text-secondary);
  }
  
  .controls-right {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    flex-shrink: 0;
  }
  
  
  /* Featured Post */
  .featured {
    padding: 3rem 0;
    background: var(--bg-secondary);
  }
  
  .featured-link {
    text-decoration: none;
    color: inherit;
    display: block;
  }
  
  .featured-link:hover .featured-post__title {
    text-decoration: underline;
    text-underline-offset: 2px;
  }
  
  .featured-post {
    display: grid;
    grid-template-columns: 500px 1fr;
    gap: 3rem;
    align-items: center;
  }
  
  .featured-post__image {
    width: 100%;
    height: 300px;
    background: var(--bg-primary);
    border: 1px solid var(--border-light);
    border-radius: 12px;
    overflow: hidden;
  }
  
  .featured-post__image img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
  
  .featured-post__tags {
    display: flex;
    gap: 0.5rem;
    margin-bottom: 1rem;
  }
  
  .post-tag {
    padding: 0.25rem 0.5rem;
    background: var(--bg-primary);
    border: 1px solid var(--border-light);
    border-radius: 4px;
    font-size: 0.75rem;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: var(--text-secondary);
  }
  
  .featured-post__title {
    font-size: 2rem;
    font-weight: 700;
    color: var(--text-primary);
    margin-bottom: 1rem;
    line-height: 1.2;
    letter-spacing: -0.02em;
  }
  
  .featured-post__description {
    font-size: 1.125rem;
    color: var(--text-secondary);
    line-height: 1.6;
    margin-bottom: 1.5rem;
  }
  
  .featured-post__meta {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 2rem;
  }
  
  .author {
    display: flex;
    align-items: center;
    gap: 0.75rem;
  }
  
  .author__avatar {
    width: 40px;
    height: 40px;
    background: var(--bg-tertiary);
    border-radius: 50%;
    overflow: hidden;
  }
  
  .author__avatar img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    filter: grayscale(100%);
  }
  
  .avatar-placeholder {
    width: 100%;
    height: 100%;
    background: var(--bg-primary);
    border-radius: 50%;
  }
  
  .author__name {
    font-weight: 600;
    color: var(--text-primary);
    font-size: 0.875rem;
  }
  
  .author__role {
    font-size: 0.813rem;
    color: var(--text-tertiary);
  }
  
  .post-meta {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 0.875rem;
    color: var(--text-tertiary);
  }
  
  .separator {
    color: var(--text-tertiary);
  }
  
  /* Posts Grid */
  .posts {
    padding: 3rem 0 4rem;
  }
  
  .posts__grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(340px, 1fr));
    gap: 2rem;
  }
  
  .post-link {
    text-decoration: none;
    color: inherit;
    display: block;
    height: 100%;
  }
  
  .post-link:hover .post-card__title {
    text-decoration: underline;
    text-underline-offset: 2px;
  }
  
  .post-card {
    height: 100%;
    display: flex;
    flex-direction: column;
    background: var(--bg-primary);
    border: 1px solid var(--border-light);
    border-radius: 12px;
    overflow: hidden;
    transition: all 0.2s ease;
  }
  
  .post-card:hover {
    border-color: var(--border-default);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  }
  
  .post-card__image {
    width: 100%;
    height: 180px;
    background: var(--bg-secondary);
    border-bottom: 1px solid var(--border-light);
    overflow: hidden;
  }
  
  .post-card__image img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
  
  .image-placeholder {
    width: 100%;
    height: 100%;
    background: linear-gradient(135deg, var(--bg-secondary) 0%, var(--bg-tertiary) 100%);
    display: flex;
    align-items: center;
    justify-content: center;
  }
  
  .image-placeholder svg {
    color: var(--text-tertiary);
    opacity: 0.3;
  }
  
  .post-card__content {
    flex: 1;
    display: flex;
    flex-direction: column;
    padding: 1.5rem;
  }
  
  .post-card__tags {
    display: flex;
    gap: 0.375rem;
    margin-bottom: 0.75rem;
  }
  
  .post-tag-small {
    padding: 0.125rem 0.375rem;
    background: var(--bg-tertiary);
    border-radius: 3px;
    font-size: 0.688rem;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: var(--text-tertiary);
  }
  
  .post-card__title {
    font-size: 1.125rem;
    font-weight: 600;
    color: var(--text-primary);
    margin-bottom: 0.75rem;
    line-height: 1.3;
  }
  
  .post-card__description {
    flex: 1;
    font-size: 0.9375rem;
    color: var(--text-secondary);
    line-height: 1.5;
    margin-bottom: 1.25rem;
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
  
  .post-card__footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding-top: 1rem;
    border-top: 1px solid var(--border-light);
  }
  
  .author-name {
    font-weight: 500;
    color: var(--text-secondary);
    font-size: 0.813rem;
  }
  
  .post-card__meta {
    display: flex;
    align-items: center;
    gap: 0.375rem;
    font-size: 0.813rem;
    color: var(--text-tertiary);
  }
  
  /* Pagination */
  .pagination-container {
    display: flex;
    justify-content: center;
    margin-top: 3rem;
    padding-top: 2rem;
    border-top: 1px solid var(--border-light);
  }
  
  /* No Results */
  .no-results {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 4rem 2rem;
    text-align: center;
  }
  
  .no-results svg {
    color: var(--text-tertiary);
    margin-bottom: 1.5rem;
  }
  
  .no-results__title {
    font-size: 1.25rem;
    font-weight: 600;
    color: var(--text-primary);
    margin-bottom: 0.5rem;
  }
  
  .no-results__description {
    font-size: 0.9375rem;
    color: var(--text-secondary);
  }
  
  /* Responsive */
  @media (max-width: 1024px) {
    .featured-post {
      grid-template-columns: 1fr;
      gap: 2rem;
    }
    
    .featured-post__image {
      height: 250px;
    }
  }
  
  @media (max-width: 768px) {
    .container {
      padding: 0 1.5rem;
    }
    
    .header {
      padding: 1rem 0;
    }
    
    .header__title {
      font-size: 1.5rem;
    }
    
    .header__content {
      flex-direction: column;
      gap: 0.5rem;
    }
    
    .header__description {
      font-size: 0.875rem;
    }
    
    .controls-row {
      flex-direction: column;
      align-items: stretch;
      gap: 1rem;
    }
    
    .controls-right {
      width: 100%;
      justify-content: space-between;
    }
    
    .tag-pills {
      justify-content: flex-start;
    }
    
    .featured-post__title {
      font-size: 1.75rem;
    }
    
    .posts__grid {
      grid-template-columns: 1fr;
      gap: 1.5rem;
    }
  }
  
  @media (max-width: 480px) {
    .header__title {
      font-size: 1.75rem;
    }
    
    .header__description {
      font-size: 1rem;
    }
    
    .tag-pills {
      overflow-x: auto;
      flex-wrap: nowrap;
      padding-bottom: 0.5rem;
    }
    
    .featured-post__title {
      font-size: 1.5rem;
    }
    
    .featured-post__description {
      font-size: 1rem;
    }
  }
</style>